import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, MapPin, Search, X } from 'lucide-react';
import { useI18n } from '@nx/i18n';
import { PARTNER_ROLE_DISPLAY, getPartnerRoleDisplay } from '../../constants/partnerRoleDisplay';
import { ProfileAvatar } from '../../components/profile/ProfileAvatar';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  discoverySearchRequested,
  setDiscoveryQuery,
  toggleDiscoveryRoleType,
  toggleDiscoveryTag,
} from '../../store/slices/discoverySlice';
import './discover.css';

export function DiscoverPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { items, total, loading, query, roleTypes, tags, allTags } = useAppSelector(
    (s) => s.discovery,
  );
  const [localQuery, setLocalQuery] = useState(query);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (localQuery !== query) {
        dispatch(setDiscoveryQuery(localQuery));
      }
      dispatch(discoverySearchRequested());
    }, 280);
    return () => window.clearTimeout(timer);
  }, [localQuery, query, roleTypes, tags, dispatch]);

  const countLabel = useMemo(() => {
    const shown = total ?? items.length;
    if (shown === 1) return t('profile.discover.countOne');
    const hasFilters = query || roleTypes.length || tags.length;
    const key = hasFilters ? 'profile.discover.countFiltered' : 'profile.discover.countShowing';
    return t(key as Parameters<typeof t>[0]).replace('{count}', String(shown));
  }, [total, items.length, query, roleTypes.length, tags.length, t]);

  return (
    <div className="discover-page">
      <div className="discover-wrap">
        <div className="discover-head">
          <p className="eyebrow">{t('profile.discover.eyebrow')}</p>
          <h1>{t('profile.discover.title')}</h1>
          <p>{t('profile.discover.subtitle')}</p>
        </div>

        <div className="discover-searchbar">
          <Search className="h-5 w-5 text-[#78665A]" strokeWidth={2} />
          <input
            placeholder={t('profile.discover.searchPlaceholder')}
            value={localQuery}
            onChange={(e) => setLocalQuery(e.target.value)}
          />
          {localQuery ? (
            <button type="button" className="clr" onClick={() => setLocalQuery('')}>
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          ) : null}
        </div>

        <div className="discover-filters">
          {PARTNER_ROLE_DISPLAY.map(({ value, profileLabelKey, icon: Icon }) => (
            <button
              key={value}
              type="button"
              className={`discover-fchip ${roleTypes.includes(value) ? 'on' : ''}`}
              onClick={() => dispatch(toggleDiscoveryRoleType(value))}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={2} />
              {t(profileLabelKey)}
            </button>
          ))}
        </div>

        {allTags.length > 0 ? (
          <div className="discover-tags">
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`discover-ftag ${tags.includes(tag) ? 'on' : ''}`}
                onClick={() => dispatch(toggleDiscoveryTag(tag))}
              >
                {tag}
              </button>
            ))}
          </div>
        ) : null}

        <p className="discover-count">{countLabel}</p>

        {loading ? (
          <div className="discover-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="discover-card discover-skel" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="discover-empty">
            <h3>{t('profile.discover.emptyTitle')}</h3>
            <p>{t('profile.discover.emptyBody')}</p>
          </div>
        ) : (
          <div className="discover-grid">
            {items.map((d) => {
              const roleDisplay = getPartnerRoleDisplay(d.roleType);
              return (
                <div
                  key={d.id}
                  className="discover-card"
                  onClick={() => navigate(`/u/${d.slug}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/u/${d.slug}`)}
                  role="button"
                  tabIndex={0}
                >
                  <div
                    className="discover-card-top"
                    style={{ '--card-accent': d.accentColor } as CSSProperties}
                  >
                    <div className={`discover-card-av${d.themeId === 'terra' ? ' sq' : ''}`}>
                      <ProfileAvatar
                        name={d.title}
                        color={d.accentColor}
                        size={52}
                        square={d.themeId === 'terra'}
                        imageUrl={d.avatarUrl}
                      />
                    </div>
                  </div>
                  <div className="discover-card-body">
                    <span className={`discover-card-id discover-card-id--${d.roleType}`}>
                      {t(roleDisplay.profileLabelKey)}
                    </span>
                    <h3>{d.title}</h3>
                    {d.location ? (
                      <p className="loc">
                        <MapPin className="h-3 w-3" strokeWidth={2} />
                        {d.location}
                      </p>
                    ) : null}
                    <p className="bio">{d.description}</p>
                    <div className="discover-card-tags">
                      {d.tags.map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                    <div className="discover-card-foot">
                      <span className="views">
                        <Eye className="h-3.5 w-3.5" strokeWidth={2} />
                        {d.viewCount.toLocaleString()}
                      </span>
                      <span className="go">
                        {t('profile.discover.viewProfile')}
                        <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
